import React, {Fragment, useCallback, useMemo, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {diApi} from '@di-shared/api/di';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {ERROR} from '@di-shared/const';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {
  createHistoryFilterSearch,
  createHistoryFilterSearchParams,
  getHistoryFilterFormState,
  HISTORY_FILTER_PERIOD_KR,
  type HistoryFilterFormStateKr,
  type HistoryFilterPeriodKr,
  type HistoryFilterQueryStateKr,
  parseHistoryFilterSearchParams,
  PERIOD_KR,
} from '@di-shared/features/history/filter/historyFilterQuery.kr';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {ClickAwayListener} from '@mui/base';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import useIntersectionObserver from '@shared/useHandleObserver';
import {useInfiniteQuery, useQuery} from '@tanstack/react-query';

interface FilteredDateHistoryResultKrProps {
  vin: string;
}

interface ResultModeContentProps {
  vin: string;
  filterState: HistoryFilterQueryStateKr;
  resultPage: number;
  onOpenFilter(): void;
  onReplaceFilterState(partialState: Partial<HistoryFilterQueryStateKr>): void;
  onReplaceResultPage(page: number): void;
}

const FILTERED_DATE_HISTORY_PAGE_SIZE = 5;
const FILTERED_DATE_HISTORY_INITIAL_PAGE = 0;

function parseResultPage(searchParams: URLSearchParams) {
  const page = Number(searchParams.get('page'));

  return Number.isFinite(page) && page >= FILTERED_DATE_HISTORY_INITIAL_PAGE ? page : FILTERED_DATE_HISTORY_INITIAL_PAGE;
}

function createResultSearchParams(filterState: HistoryFilterQueryStateKr, page: number) {
  const params = createHistoryFilterSearchParams(filterState);

  if (page > FILTERED_DATE_HISTORY_INITIAL_PAGE) {
    params.set('page', String(page));
  }

  return params;
}

function getPeriodLabel(
  period: HistoryFilterPeriodKr,
  form: HistoryFilterFormStateKr,
  t: ReturnType<typeof useInferableTranslation>['t'],
  CldrDatetimeFormatter: ReturnType<typeof useCldrDatetimeFormatter>
) {
  switch (period) {
    case HISTORY_FILTER_PERIOD_KR.SELECT_MONTH:
      return form.startDate ? CldrDatetimeFormatter.format(form.startDate, 'yMMM') : '';
    case HISTORY_FILTER_PERIOD_KR.RECENT_30_DAYS:
      return t('T20434_$days', {days: PERIOD_KR.RECENT_30_DAYS});
    case HISTORY_FILTER_PERIOD_KR.RECENT_90_DAYS:
      return t('T20434_$days', {days: PERIOD_KR.RECENT_90_DAYS});
    case HISTORY_FILTER_PERIOD_KR.CUSTOM_DAYS:
      return t('T10165');
  }
}

function ResultModeErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <div className="trip_info">
      <div className="empty_area">
        <span className="ic ic_caution_32px"></span>
        <span
          className="body_large_r"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10175')}
        </span>
      </div>
    </div>
  );
}

export default function ({vin}: FilteredDateHistoryResultKrProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterState = useMemo(() => parseHistoryFilterSearchParams(searchParams), [searchParams]);
  const resultPage = useMemo(() => parseResultPage(searchParams), [searchParams]);

  function replaceFilterState(partialState: Partial<HistoryFilterQueryStateKr>) {
    const nextState = {
      ...filterState,
      ...partialState,
    };

    setSearchParams(createResultSearchParams(nextState, FILTERED_DATE_HISTORY_INITIAL_PAGE), {replace: true});
  }

  function replaceResultPage(page: number) {
    setSearchParams(createResultSearchParams(filterState, page), {replace: true});
  }

  function navigateToFilter() {
    navigate(
      {
        pathname: ROUTE_PATHS.HISTORY_FILTERED_FILTER.getFullPath(vin),
        search: createHistoryFilterSearch(filterState),
      },
      {replace: true}
    );
  }

  return (
    <ErrorBoundary fallback={<ResultModeErrorFallback />}>
      <InnerResultMode
        vin={vin}
        filterState={filterState}
        resultPage={resultPage}
        onOpenFilter={navigateToFilter}
        onReplaceFilterState={replaceFilterState}
        onReplaceResultPage={replaceResultPage}
      />
    </ErrorBoundary>
  );
}

function InnerResultMode({vin, filterState, resultPage, onOpenFilter, onReplaceFilterState, onReplaceResultPage}: ResultModeContentProps) {
  const navigate = useNavigate();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol, convertSpeedValue, convertSpeedSymbol} = useUnitConverter();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const filterForm = useMemo(() => getHistoryFilterFormState(filterState), [filterState]);
  const selectedPeriodLabel = getPeriodLabel(filterForm.period, filterForm, t, CldrDatetimeFormatter);
  const {startDate, endDate, tagIds} = filterForm;

  const {data: allTags} = useQuery({
    queryKey: [getQueryKey(diApi.tripTags.getAllTags)],
    queryFn: () => diApi.tripTags.getAllTags(),
    select: ({data}) => data.tags,
  });
  const tagNameById = useMemo(() => new Map((allTags ?? []).map((tag) => [tag.tagId, tag.tagName])), [allTags]);

  const {data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError} = useInfiniteQuery({
    queryKey: [
      getQueryKey(diApi.driveHistory.getFilteredTripHistory),
      vin,
      startDate ? getYYYYMMddFormat(startDate) : null,
      endDate ? getYYYYMMddFormat(endDate) : null,
      tagIds,
    ],
    queryFn: ({pageParam = FILTERED_DATE_HISTORY_INITIAL_PAGE}) =>
      diApi.driveHistory.getFilteredTripHistory({
        vin,
        startDate: startDate ? getYYYYMMddFormat(startDate) : undefined,
        endDate: endDate ? getYYYYMMddFormat(endDate) : undefined,
        tagIds: tagIds.length > 0 ? tagIds : undefined,
        orderBy: 'ASC',
        page: pageParam,
        size: FILTERED_DATE_HISTORY_PAGE_SIZE,
      }),
    initialPageParam: FILTERED_DATE_HISTORY_INITIAL_PAGE,
    getNextPageParam: (lastPage) => {
      const pageInfo = lastPage.data?.pageInfo;

      return pageInfo && pageInfo.currentPage < pageInfo.totalPages ? pageInfo.currentPage + 1 : undefined;
    },
  });

  const pages = data?.pages || [];
  const currentLoadedPage = pages.length;
  const firstPage = pages[0]?.data;
  const tripItems = pages.flatMap((page) => page.data?.content ?? []);
  const fallbackSummaryData = tripItems.reduce(
    (summary, trip) => ({
      totalDistance: summary.totalDistance + (trip.drvDist ?? 0),
      totalDrivingSeconds: summary.totalDrivingSeconds + (trip.drvTime ?? 0) * 60,
    }),
    {totalDistance: 0, totalDrivingSeconds: 0}
  );
  const totalDistance = firstPage?.totalDistance ?? fallbackSummaryData.totalDistance;
  const totalDrivingSeconds = firstPage?.totalRunTime ?? fallbackSummaryData.totalDrivingSeconds;
  const totalDrivingHours = Math.floor(totalDrivingSeconds / 3600);
  const totalDrivingMinutes = Math.floor((totalDrivingSeconds % 3600) / 60);
  const isTotalDrivingLessThanOneMinute = totalDrivingSeconds < 60;
  const isEmpty = !isLoading && !isError && firstPage?.pageInfo?.totalElements === 0;
  const shouldShowSummaryTimePlaceholder = isLoading || isError || isEmpty;

  const handleObserver = useCallback(
    async (entry: IntersectionObserverEntry) => {
      if (!entry.isIntersecting || isFetchingNextPage || !hasNextPage) {
        return;
      }

      const nextPage = currentLoadedPage + 1;
      await fetchNextPage();
      onReplaceResultPage(nextPage);
    },
    [currentLoadedPage, fetchNextPage, hasNextPage, isFetchingNextPage, onReplaceResultPage]
  );

  const {observerRef} = useIntersectionObserver(handleObserver, {threshold: 0});

  if (!startDate || !endDate || startDate > endDate) {
    return null;
  }

  return (
    <div className="wrap">
      <Header
        left={<HeaderBackButton />}
        right={
          <ClickAwayListener onClickAway={() => setIsMoreMenuOpen(false)}>
            <div className="btn_more_wrap">
              <button
                type="button"
                className="btn btn_ic"
                aria-label={t('T20134')}
                aria-expanded={isMoreMenuOpen}
                aria-haspopup="menu"
                onClick={() => setIsMoreMenuOpen((prev) => !prev)}
              >
                <span
                  className="ic ic_menu_24px"
                  aria-hidden="true"
                ></span>
              </button>
              {isMoreMenuOpen && (
                <ul
                  className="more_menu"
                  role="menu"
                >
                  <li
                    role="none"
                    className="more_menu_item"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setIsMoreMenuOpen(false);
                        navigate({pathname: ROUTE_PATHS.HISTORY_DOWNLOAD.getFullPath(vin), search: createHistoryFilterSearch(filterState)});
                      }}
                    >
                      {t('T10089')}
                    </button>
                  </li>
                  <li
                    role="none"
                    className="more_menu_item"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setIsMoreMenuOpen(false);
                        navigate(ROUTE_PATHS.HISTORY_TAGS.getFullPath(vin));
                      }}
                    >
                      {t('T20415')}
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </ClickAwayListener>
        }
      >
        <h1 className="title_xsmall_b nav_center_title">
          <span className="blind">{t('T10098')}</span>
        </h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section section_filter_complete">
          <div className="filter_setting">
            <div className="chips">
              <button
                type="button"
                className="chip chip_filter_line"
                onClick={onOpenFilter}
              >
                <span className="ic ic_filter_20px"></span>
                <span className="body_small_r" />
              </button>
              <button
                type="button"
                className="chip"
                onClick={onOpenFilter}
              >
                <span className="body_small_r">{selectedPeriodLabel}</span>
                <span className="ic ic_arrow_down_16px"></span>
              </button>
              {filterState.tagIds.map((tagId) => {
                const tagName = tagNameById.get(tagId) ?? tagId;

                return (
                  <button
                    key={tagId}
                    type="button"
                    className="chip"
                    onClick={() => onReplaceFilterState({tagIds: filterState.tagIds.filter((id) => id !== tagId)})}
                  >
                    <span className="body_small_r">{tagName}</span>
                    <span className="ic ic_close_12px"></span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="driving_summary">
            <div className="driving_date">
              <span className="date_text">{`${CldrDatetimeFormatter.format(startDate, 'yMMMd')} - ${CldrDatetimeFormatter.format(endDate, 'yMMMd')}`}</span>
            </div>
            <dl className="driving_summary_list">
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10053')}</dt>
                <dd className="driving_summary_info">
                  {shouldShowSummaryTimePlaceholder && (
                    <>
                      <span className="driving_summary_value">{ERROR.DASH}</span>
                      <span className="driving_summary_unit">{t('T10054')}</span>
                      <span className="driving_summary_value">{ERROR.DASH}</span>
                      <span className="driving_summary_unit">{t('T10055')}</span>
                    </>
                  )}
                  {!shouldShowSummaryTimePlaceholder && !!totalDrivingHours && (
                    <>
                      <span className="driving_summary_value">{totalDrivingHours}</span>
                      <span className="driving_summary_unit">{t('T10054')}</span>
                    </>
                  )}
                  {!shouldShowSummaryTimePlaceholder && !!totalDrivingMinutes && (
                    <>
                      <span className="driving_summary_value">{totalDrivingMinutes}</span>
                      <span className="driving_summary_unit">{t('T10055')}</span>
                    </>
                  )}
                  {!shouldShowSummaryTimePlaceholder && isTotalDrivingLessThanOneMinute && (
                    <span className="driving_summary_value">{t('T10202')}</span>
                  )}
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('T10052')}</dt>
                <dd className="driving_summary_info">
                  {totalDistance < 1 ? (
                    <span className="driving_summary_value">{t('T20341')}</span>
                  ) : (
                    <>
                      <span className="driving_summary_value">{formatNumberWithCommas(convertDistanceValue(totalDistance))}</span>
                      <span className="driving_summary_unit">{convertDistanceSymbol(totalDistance)}</span>
                    </>
                  )}
                </dd>
              </div>
            </dl>
          </div>
          {isLoading && (
            <div className="page_loading_box">
              <div className="loading_lottie">
                <LottieSelector
                  id="img_json_loading_black_60f"
                  kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  loop={true}
                  autoplay={true}
                  style={{width: 24, height: 24}}
                />
              </div>
              <span className="loading_text">{t('T10185')}</span>
            </div>
          )}
          {isError && <ResultModeErrorFallback />}
          {isEmpty && (
            <div className="trip_info">
              <div className="empty_area">
                <span className="ic ic_caution_32px"></span>
                <span className="body_large_r">{t('T10067')}</span>
              </div>
            </div>
          )}
          {tripItems.map((trip) => {
            const ignitionOnDate = parseDateWithFormat(trip.ignOnTime);
            const ignitionOffDate = parseDateWithFormat(trip.ignOffTime);
            const drvTime = trip.drvTime ?? 0;
            const intervalIgnitionHours = Math.floor(drvTime / 60);
            const intervalIgnitionMinutes = drvTime % 60;

            return (
              <Fragment key={trip.tripId}>
                <div className="trip_info type_badge">
                  <span className="blind">trip info detail</span>
                  <div className="trip_info_head">
                    <span className="trip_info_item">
                      {ignitionOnDate && ignitionOffDate
                        ? DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')
                        : ERROR.DASH}
                    </span>
                    {trip.tags
                      ?.filter(({tagType}) => tagType === 'BUSINESS')
                      .map(({tagId, tagName}) => (
                        <span
                          key={tagId}
                          className="badge badge_basic_container"
                        >
                          {tagName}
                        </span>
                      ))}
                  </div>
                </div>
                <div className="statistics_record_area">
                  <ul className="statistics_record_list">
                    <li className="statistics_record_item">
                      <span className="statistics_record_key">
                        <span className="ic ic_time_16px"></span>
                        {t('T10053')}
                      </span>
                      <span className="statistics_record_val">
                        {!!intervalIgnitionHours && (
                          <>
                            <span className="statistics_record_number">{intervalIgnitionHours}</span>
                            <span className="statistics_record_unit">{t('T10054')}</span>
                          </>
                        )}
                        {!!intervalIgnitionMinutes && (
                          <>
                            <span className="statistics_record_number">{intervalIgnitionMinutes}</span>
                            <span className="statistics_record_unit">{t('T10055')}</span>
                          </>
                        )}
                        {!intervalIgnitionHours && !intervalIgnitionMinutes && (
                          <span className="statistics_record_number">{t('T10202')}</span>
                        )}
                      </span>
                    </li>
                    <li className="statistics_record_item">
                      <span className="statistics_record_key">
                        <span className="ic ic_drive_distance_16px"></span>
                        {t('T10052')}
                      </span>
                      <span className="statistics_record_val">
                        {(trip.drvDist ?? 0) < 1 ? (
                          <span className="statistics_record_unit">{t('T20341')}</span>
                        ) : (
                          <>
                            <span className="statistics_record_number">
                              {formatNumberWithCommas(convertDistanceValue(trip.drvDist ?? 0))}
                            </span>
                            <span className="statistics_record_unit">{convertDistanceSymbol(trip.drvDist ?? 0)}</span>
                          </>
                        )}
                      </span>
                    </li>
                    <li className="statistics_record_item">
                      <span className="statistics_record_key">
                        <span className="ic ic_avgspeed_16px"></span>
                        {t('T10206')}
                      </span>
                      <span className="statistics_record_val">
                        <span className="statistics_record_number">{formatNumberWithCommas(convertSpeedValue(trip.maxSpeed ?? 0))}</span>
                        <span className="statistics_record_unit">{convertSpeedSymbol(trip.maxSpeed ?? 0)}</span>
                      </span>
                    </li>
                  </ul>
                  <button
                    type="button"
                    className="btn_learn_more"
                    onClick={() => navigate(ROUTE_PATHS.HISTORY_$TRIP_ID.getFullPath(vin, trip.tripId))}
                  >
                    {t('T10063')}
                    <span className="ic ic_arrow_forward_12px"></span>
                  </button>
                </div>
              </Fragment>
            );
          })}
          {(isFetchingNextPage || hasNextPage) && (
            <div
              className="page_loading_box"
              ref={observerRef}
            >
              <div className="loading_lottie">
                <LottieSelector
                  id="img_json_loading_black_60f"
                  kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  loop={true}
                  autoplay={true}
                  style={{width: 24, height: 24}}
                />
              </div>
              <span className="loading_text">{t('T10185')}</span>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
